/*
  Окно «Отправить СМС выбранным» - вариант Б, решение владельца 06.10.2026.

  Отвечает на три вопроса подряд: какими словами - кому уйдёт - во что
  обойдётся. Разбор пересчитывается сам при смене формулировки и согласия на
  длинные сообщения: отдельная кнопка «Разобрать» была бы лишним шагом перед
  тем, что и так нужно увидеть до отправки.

  Отправка - то же действие, что раньше у карточки «Рассылка сообщений»: те же
  правила частоты, подпись юриста и цена. Изменилось одно - кому: теперь это
  отмеченные в таблице должники, а не вся зона «работа с должником».
*/

import { useEffect, useState } from 'react'

import { api } from '../api/client.js'
import { plural } from '../api/format.js'
import { Check, Field, Loading, Notice } from './Common.jsx'
import MessagePlan from './MessagePlan.jsx'
import Modal from './Modal.jsx'
import Dropdown from './Select.jsx'

const messages = (count) => plural(count, 'сообщение', 'сообщения', 'сообщений')

/*
  Согласие на сообщение длиннее одной части. Без галочки не уложившееся
  заменяется нейтральным уведомлением (решение владельца 29.09.2026); галочка
  нужна двум приёмам, которые в одну часть не помещаются по существу.
*/
const LONG_HINT =
  'Без галочки сообщение, не уложившееся в одну часть, уходит нейтральным уведомлением - рассылка стоит одну часть на получателя. С галочкой оно уходит выбранными словами целиком, и каждая часть оплачивается отдельно.'

export default function SmsWaveDialog({ chosen, onClose, onSent }) {
  const [templates, setTemplates] = useState(null)
  const [choice, setChoice] = useState({ key: '', multipart: false })
  const [plan, setPlan] = useState(null)
  const [state, setState] = useState({ busy: false, refused: null, error: null })
  const accountIds = chosen.map((row) => row.account_id)
  const idsKey = accountIds.join(',')

  useEffect(() => {
    api
      .messageTemplates()
      .then((library) => {
        setTemplates(Object.fromEntries(library.templates.map((row) => [row.key, row.title])))
        setChoice((now) => ({ ...now, key: now.key || library.templates[0]?.key || '' }))
      })
      .catch((error) => setState((now) => ({ ...now, error })))
  }, [])

  useEffect(() => {
    if (!choice.key) return undefined
    let current = true
    setPlan(null)
    api
      .planMessageWave({
        template_key: choice.key,
        account_ids: idsKey.split(','),
        allow_multipart: choice.multipart,
      })
      .then((answer) => current && setPlan(answer))
      .catch((error) => current && setState((now) => ({ ...now, error })))
    return () => {
      current = false
    }
  }, [choice, idsKey])

  async function send() {
    setState({ busy: true, refused: null, error: null })
    try {
      const sent = await api.sendMessageWave({
        template_key: choice.key,
        account_ids: accountIds,
        allow_multipart: choice.multipart,
      })
      if (sent.refused) {
        setState({ busy: false, refused: sent.refused, error: null })
      } else {
        onSent(sent)
      }
    } catch (error) {
      setState({ busy: false, refused: null, error })
    }
  }

  return (
    <Modal title={`Отправить СМС выбранным: ${chosen.length}`} onClose={onClose} busy={state.busy}>
      <div className="stack">
        {templates ? (
          <>
            <div className="field field--l">
              <span className="field__label" id="sms-wave-template">
                Формулировка
              </span>
              <Dropdown
                value={choice.key}
                onChange={(key) => setChoice((now) => ({ ...now, key }))}
                options={templates}
                labelledBy="sms-wave-template"
              />
            </div>
            {/* Галочка отдельной строкой и с пояснением под значком - так же,
                как в настройках рассылки: рядом со списком она не вставала
                на одну линию с ним (осмотр окна 06.10.2026). */}
            <Field
              label="Разрешить длинные сообщения"
              width="m"
              className="field--check"
              hint={LONG_HINT}
            >
              <Check
                checked={choice.multipart}
                onChange={(multipart) => setChoice((now) => ({ ...now, multipart }))}
                title="Отправлять выбранными словами, даже если сообщение занимает несколько частей"
              />
            </Field>
          </>
        ) : null}

        {choice.key && !plan && !state.error ? <Loading what="Считаем охват и цену" /> : null}
        {plan ? <MessagePlan plan={plan} /> : null}

        {state.refused ? (
          <Notice kind="warn">
            <div className="notice__what">Сообщения не отправлены.</div>
            <div className="notice__do">{state.refused}</div>
          </Notice>
        ) : null}
        <Notice error={state.error} />
        <div className="row">
          <button
            type="button"
            className="button"
            disabled={state.busy || !plan?.can_send}
            onClick={send}
          >
            {state.busy
              ? 'Отправляем…'
              : plan
                ? `Отправить ${messages(plan.drafts.length)}`
                : 'Отправить'}
          </button>
          <button type="button" className="button button--quiet" onClick={onClose}>
            Отмена
          </button>
        </div>
      </div>
    </Modal>
  )
}
